import React, {useRef, useState} from 'react';
import {
    ActionIcon,
    Button,
    Group,
    Paper,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import {IconTrash} from '@tabler/icons-react';
import {notifications} from '@mantine/notifications';
import {useQueryClient} from '@tanstack/react-query';
import {
    RegistrationMailService,
    type RegistrationMailAttachment,
    type RegistrationMailTemplate,
} from '../../client';
import {formatRequestError} from '../../utils/formatRequestError';
import {
    formatAttachmentSize,
    MAX_TOTAL_BYTES,
    remainingAttachmentBytes,
    totalAttachmentSize,
} from './registrationMailAttachments';

type RegistrationMailAttachmentsProps = {
    attachments: RegistrationMailAttachment[];
    disabled?: boolean;
    onAttachmentsChange: (template: RegistrationMailTemplate) => void;
};

export function RegistrationMailAttachments({
    attachments,
    disabled = false,
    onAttachmentsChange,
}: RegistrationMailAttachmentsProps) {
    const queryClient = useQueryClient();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const usedBytes = totalAttachmentSize(attachments);
    const remainingBytes = remainingAttachmentBytes(attachments);

    const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }

        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            notifications.show({
                color: 'red',
                title: 'Invalid file type',
                message: 'Only PDF files are allowed.',
            });
            return;
        }

        if (file.size > remainingBytes) {
            notifications.show({
                color: 'red',
                title: 'Attachment too large',
                message: `Total attachment size must not exceed ${formatAttachmentSize(MAX_TOTAL_BYTES)}.`,
            });
            return;
        }

        setUploading(true);
        RegistrationMailService.uploadRegistrationMailAttachmentApiV1RegistrationMailTemplateAttachmentsPost({
            formData: {file},
        })
            .then((result) => {
                onAttachmentsChange(result);
                queryClient.setQueryData(['registration-mail-template'], result);
                notifications.show({
                    color: 'green',
                    title: 'Attachment uploaded',
                    message: `${file.name} was added to the mail template.`,
                });
            })
            .catch((error: unknown) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not upload attachment',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setUploading(false));
    };

    const deleteAttachment = (attachmentId: string) => {
        setDeletingId(attachmentId);
        RegistrationMailService.deleteRegistrationMailAttachmentApiV1RegistrationMailTemplateAttachmentsAttachmentIdDelete({
            attachmentId,
        })
            .then((result) => {
                onAttachmentsChange(result);
                queryClient.setQueryData(['registration-mail-template'], result);
                notifications.show({
                    color: 'green',
                    title: 'Attachment removed',
                    message: 'The attachment was deleted.',
                });
            })
            .catch((error: unknown) => {
                notifications.show({
                    color: 'red',
                    title: 'Could not remove attachment',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setDeletingId(null));
    };

    return (
        <Paper withBorder p="md" radius="md">
            <Stack gap="sm">
                <Title order={5}>PDF attachments</Title>
                <Text size="sm" c="dimmed">
                    Attachments are saved immediately and included in test and bulk sends.
                    Only PDF files are allowed (max. {formatAttachmentSize(MAX_TOTAL_BYTES)} total).
                </Text>
                <Text size="sm">
                    {formatAttachmentSize(usedBytes)} / {formatAttachmentSize(MAX_TOTAL_BYTES)} used
                </Text>
                {attachments.length > 0 ? (
                    <Stack gap="xs">
                        {attachments.map((attachment) => (
                            <Group key={attachment.id} justify="space-between" wrap="nowrap">
                                <div>
                                    <Text size="sm">{attachment.original_name}</Text>
                                    <Text size="xs" c="dimmed">
                                        {formatAttachmentSize(attachment.size)}
                                    </Text>
                                </div>
                                <ActionIcon
                                    variant="subtle"
                                    color="red"
                                    aria-label={`Remove ${attachment.original_name}`}
                                    loading={deletingId === attachment.id}
                                    disabled={disabled || uploading}
                                    onClick={() => deleteAttachment(attachment.id)}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Group>
                        ))}
                    </Stack>
                ) : (
                    <Text size="sm" c="dimmed">No attachments yet.</Text>
                )}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    hidden
                    onChange={handleFileSelected}
                />
                <Button
                    variant="outline"
                    loading={uploading}
                    disabled={disabled || remainingBytes === 0}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Add PDF
                </Button>
            </Stack>
        </Paper>
    );
}
